/**
 * SignalSelectorModal - Modal to select a signal for buy/sell conditions
 * With view details functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Input, Card, Tag, Empty, Spin, Row, Col, Button, Tabs, Space, Typography } from 'antd';
import { SearchOutlined, EyeOutlined, CheckOutlined, DashboardOutlined, HeatMapOutlined, LineChartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';
import { SavedSignal } from './types';
import { getConfigAsync } from '../../utils/config';
import { getApiHeadersWithoutContentType } from '../../utils/apiHeaders';
import { formatTimestamp } from '../../utils/timeUtils';
import Dashboard from '../Analysis/Dashboard';
import HeatmapTable from '../Analysis/HeatmapTable';
import AverageReturnTable from '../Analysis/AverageReturnTable';

const { Text } = Typography;

interface SignalSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (signal: SavedSignal) => void;
  title?: string;
  excludeSignalIds?: string[];
  filterSymbol?: string;  // 过滤特定交易对
  filterTimeframe?: string;  // 过滤特定时间周期
}

// Cache for signals to avoid repeated API calls
let signalsCache: SavedSignal[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 30000; // 30 seconds cache

const SignalSelectorModal: React.FC<SignalSelectorModalProps> = ({
  open,
  onClose,
  onSelect,
  title = 'Add Signal',
  excludeSignalIds = [],
  filterSymbol,
  filterTimeframe
}) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [signals, setSignals] = useState<SavedSignal[]>(signalsCache || []);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingSignal, setViewingSignal] = useState<SavedSignal | null>(null);

  // Load signals when modal opens (with caching)
  useEffect(() => {
    if (open) {
      const now = Date.now();
      // Use cache if valid
      if (signalsCache && (now - cacheTimestamp) < CACHE_DURATION) {
        setSignals(signalsCache);
      } else {
        loadSignals();
      }
    }
  }, [open]);

  const loadSignals = useCallback(async () => {
    setLoading(true);
    try {
      const config = await getConfigAsync();
      const API_BASE_URL = config.API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/signal/list`, {
        headers: getApiHeadersWithoutContentType()
      });
      const data = await response.json();
      if (data.success) {
        setSignals(data.signals);
        // Update cache
        signalsCache = data.signals;
        cacheTimestamp = Date.now();
      }
    } catch (error) {
      console.error('Failed to load signals:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter signals based on search term, excluded IDs, symbol and timeframe
  const filteredSignals = signals.filter(signal => {
    // 排除已选中的信号
    if (excludeSignalIds.includes(signal.id)) return false;
    
    // 按 symbol 过滤（如果指定）
    if (filterSymbol && signal.symbol !== filterSymbol) return false;
    
    // 按 timeframe 过滤（如果指定）
    if (filterTimeframe && signal.timeframe !== filterTimeframe) return false;
    
    // 搜索词过滤
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      signal.name.toLowerCase().includes(term) ||
      signal.symbol.toLowerCase().includes(term) ||
      (signal.description?.toLowerCase().includes(term))
    );
  });

  const handleSelect = (signal: SavedSignal) => {
    onSelect(signal);
    onClose();
    setSearchTerm('');
    setViewingSignal(null);
  };

  const handleClose = () => {
    onClose();
    setSearchTerm('');
    setViewingSignal(null);
  };

  const getSignalTypeColor = (type?: string) => {
    switch (type) {
      case 'point': return '#722ed1';
      case 'rectangle': return '#1890ff';
      default: return '#52c41a';
    }
  };

  // Signal details view
  const renderSignalDetails = () => {
    if (!viewingSignal) return null;

    const analysisData = {
      returns_analysis: viewingSignal.analysis_data?.returns_analysis || null
    };

    return (
      <div>
        {/* Signal Info Header */}
        <Card
          size="small"
          style={{ 
            marginBottom: 16, 
            background: theme.toolbarBackground, 
            borderColor: theme.surfaceBorder 
          }}
        >
          <Row gutter={[16, 8]}>
            <Col span={8}>
              <Text type="secondary">{t('signal.symbol', 'Symbol')}:</Text>
              <div style={{ fontWeight: 600 }}>{viewingSignal.symbol}</div>
            </Col>
            <Col span={8}>
              <Text type="secondary">{t('signal.timeframe', 'Timeframe')}:</Text>
              <div style={{ fontWeight: 600 }}>{viewingSignal.timeframe}</div>
            </Col>
            <Col span={8}>
              <Text type="secondary">{t('signal.timeRange', 'Time Range')}:</Text>
              <div style={{ fontSize: 12 }}>
                {formatTimestamp(viewingSignal.time_range.start_time)}
                <br />~ {formatTimestamp(viewingSignal.time_range.end_time)}
              </div>
            </Col>
          </Row>
          {viewingSignal.description && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{t('signal.description', 'Description')}:</Text>
              <div>{viewingSignal.description}</div>
            </div>
          )}
        </Card>

        {/* Analysis Results */}
        {analysisData.returns_analysis ? (
          <Tabs
            defaultActiveKey="dashboard"
            size="small"
            items={[
              {
                key: 'dashboard',
                label: <span><DashboardOutlined /> {t('analysis.performanceDashboard', 'Dashboard')}</span>,
                children: <Dashboard returnsAnalysis={analysisData} />
              },
              {
                key: 'heatmap',
                label: <span><HeatMapOutlined /> {t('analysis.winRateHeatmap', 'Win Rate')}</span>,
                children: <HeatmapTable returnsAnalysis={analysisData} />
              },
              {
                key: 'returns',
                label: <span><LineChartOutlined /> {t('analysis.averageReturnTable', 'Returns')}</span>,
                children: <AverageReturnTable returnsAnalysis={analysisData} />
              }
            ]}
          />
        ) : (
          <Empty description={t('analysis.noAnalysisData', 'No analysis data')} />
        )}
      </div>
    );
  };

  return (
    <Modal
      title={viewingSignal ? (
        <Space>
          <Button 
            type="text" 
            size="small"
            onClick={() => setViewingSignal(null)}
          >
            ← {t('common.back', 'Back')}
          </Button>
          {viewingSignal.name}
        </Space>
      ) : title}
      open={open}
      onCancel={handleClose}
      footer={viewingSignal ? [
        <Button key="back" onClick={() => setViewingSignal(null)}>
          {t('common.back', 'Back')}
        </Button>,
        <Button 
          key="select" 
          type="primary" 
          icon={<CheckOutlined />}
          onClick={() => handleSelect(viewingSignal)}
        >
          {t('strategyBuilder.selectThisSignal', 'Select This Signal')}
        </Button>
      ] : null}
      width={viewingSignal ? 900 : 700}
      centered
      destroyOnClose={false}
      styles={{
        content: {
          background: theme.surfaceBackground,
          borderRadius: 12,
        },
        header: {
          background: theme.surfaceBackground,
          borderBottom: `1px solid ${theme.surfaceBorder}`,
        },
        body: {
          maxHeight: '70vh',
          overflowY: 'auto'
        }
      }}
    >
      {viewingSignal ? (
        renderSignalDetails()
      ) : (
        <>
          {/* Search Input */}
          <Input
            placeholder={t('strategyBuilder.searchSignals', 'Search signals...')}
            prefix={<SearchOutlined style={{ color: theme.textSecondary }} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              marginBottom: 16,
              background: theme.toolbarBackground,
              borderColor: theme.surfaceBorder
            }}
          />

          {/* Signals Grid */}
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin />
              </div>
            ) : filteredSignals.length === 0 ? (
              <Empty
                description={signals.length === 0 
                  ? t('strategyBuilder.noSignalsSaved', 'No signals saved yet. Create signals from Chart Analysis first.')
                  : (filterSymbol || filterTimeframe) 
                    ? t('strategyBuilder.noSignalsForFilter', `No signals found for ${filterSymbol || ''} ${filterTimeframe || ''}. Create matching signals first.`)
                    : t('strategyBuilder.noSignalsMatch', 'No signals match your search')
                }
              />
            ) : (
              <Row gutter={[12, 12]}>
                {filteredSignals.map((signal) => (
                  <Col span={12} key={signal.id}>
                    <Card
                      size="small"
                      hoverable
                      style={{
                        background: theme.toolbarBackground,
                        borderColor: theme.surfaceBorder,
                        borderRadius: 8,
                      }}
                      styles={{
                        body: { padding: 12 }
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ 
                          fontWeight: 600, 
                          color: theme.textPrimary,
                          fontSize: 14
                        }}>
                          {signal.name}
                        </span>
                        <Tag 
                          color={getSignalTypeColor(signal.signal_type)}
                          style={{ marginLeft: 8, fontSize: 10 }}
                        >
                          {signal.signal_type === 'point' ? 'Point' : 'Rectangle'}
                        </Tag>
                      </div>
                      <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>
                        {signal.symbol} • {signal.timeframe}
                      </div>
                      {signal.description && (
                        <div style={{ 
                          color: theme.textSecondary, 
                          fontSize: 11,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginBottom: 4
                        }}>
                          {signal.description}
                        </div>
                      )}
                      <div style={{ 
                        color: theme.textSecondary, 
                        fontSize: 10, 
                        marginBottom: 8,
                        opacity: 0.7
                      }}>
                        {formatTimestamp(signal.time_range.start_time)} - {formatTimestamp(signal.time_range.end_time)}
                      </div>
                      
                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingSignal(signal);
                          }}
                          style={{ flex: 1 }}
                        >
                          {t('signal.view', 'View')}
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(signal);
                          }}
                          style={{ flex: 1 }}
                        >
                          {t('common.select', 'Select')}
                        </Button>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default SignalSelectorModal;
